import {
  Button,
  Box,
  IconButton,
  Modal,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import DataTable from '../../../../components/table/datatable';
import { Close, HelpOutline } from '@mui/icons-material';
import { getColor } from '../../../../assets/theme';
import { GridColDef } from '@mui/x-data-grid';
import { memo, useEffect, useState } from 'react';
import { useGetLogsByFolioQuery } from '../../../../api/front-office/logs.api.rtk';
import { IFAPILogDetails } from '../../interface/fo-logs.interface';
import ServerError from '../../../../components/animation/server-error';

const DataTableColumns: GridColDef[] = [
  {
    field: 'user',
    headerName: 'User',
    minWidth: 200,
    hideable: false,
    sortable: false,
  },
  {
    field: 'action',
    headerName: 'Action',
    minWidth: 400,
    hideable: false,
    sortable: false,
  },
  {
    field: 'timestamp',
    headerName: 'Timestamp',
    minWidth: 200,
    hideable: false,
    sortable: false,
  },
];

const ProfileLogsModal = ({
  isOpen,
  onClose,
  profileID,
}: {
  isOpen: boolean;
  onClose: () => void;
  profileID?: string | null | undefined;
}) => {
  const { primary, textGray } = getColor();
  const [logsData, setLogsDataTable] = useState<IFAPILogDetails[]>(
    [] as IFAPILogDetails[],
  );

  const {
    data: profileLogs,
    isError,
    isLoading,
  } = useGetLogsByFolioQuery(`${profileID}`, {
    skip: !isOpen || !profileID,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (!Array.isArray(profileLogs) || profileLogs.length === 0) return;
    const profileDetailedLogs = profileLogs.map((log) => log);
    setLogsDataTable(profileDetailedLogs);
  }, [isOpen, profileLogs]);

  return (
    <Modal
      hideBackdrop={true}
      open={isOpen}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'rgba(0, 0, 0, 0.5)',
      }}
    >
      <Box
        sx={{
          p: '24px',
          position: 'fixed',
          transform: 'translate(0%, 0%)',
          maxWidth: '90vw',
          minWidth: { xs: '90vw', md: '820px' },
          height: 'auto',
          maxHeight: '90vh',
          bgcolor: 'background.paper',
          boxShadow: 24,
          overflow: 'auto',
        }}
      >
        <Stack
          direction='row'
          justifyContent='space-between'
          borderBottom='1px solid #EEE'
          p='10px 0'
        >
          <Typography
            variant='h5'
            component='div'
            sx={{ flexGrow: 1, color: '#000000' }}
          >
            Logs
            <Tooltip title='Learn More' placement='right-end'>
              <IconButton
                sx={{
                  border: 0,
                  color: textGray,
                  bgcolor: 'transparent',
                  ':hover': {
                    color: primary,
                    bgcolor: 'transparent',
                  },
                }}
                disableRipple
              >
                <HelpOutline />
              </IconButton>
            </Tooltip>
          </Typography>
          <IconButton color='inherit' onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
        <Box mb={1} />

        {isError ? (
          <ServerError />
        ) : (
          <DataTable
            getRowId={(row) => row.id}
            columns={DataTableColumns}
            rows={logsData || []}
            loading={isLoading} //
            disableColumnFilter
            disableColumnMenu
            disableColumnSelector
            pageSize={20}
            autoPageSize={false}
            checkboxSelection={false}
            tableStyle={{
              padding: 0,
              '&.MuiDataGrid-root': {
                p: 0,
              },
              '& .MuiTablePagination-root': {
                p: '0 10px',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'transparent',
                borderRadius: 0,
              },
              ".MuiButtonBase-root[aria-label='Export']": {
                display: 'none!important',
              },
              '.MuiDataGrid-root .MuiDataGrid-toolbar': {
                justifyContent: 'flex-start',
              },
            }}
            height={500}
          />
        )}

        <Stack
          direction='row'
          justifyContent='flex-end'
          borderBottom='1px solid #EEE'
        ></Stack>
        <Box mb={2} />
        <Stack direction='row' gap={1} justifyContent='flex-end'>
          <Box flexGrow={1} />
          <Button
            variant='outlined'
            sx={{
              width: '74px',
              height: '36px',
              border: '1px solid',
              backgroundColor: 'transparent',
            }}
            onClick={onClose}
          >
            Close
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};

export default memo(ProfileLogsModal);
